// Maps a refused challenge call to a user-facing outcome (HIA-BUG-20260929-4).
//
// Two phases share one vocabulary (`kind`):
//   - CREATE: POST /back-tenant/api/v1/client-users/challenges (back-tenant maps a refusal to
//     423 locked / 422 convention pending / 404 unknown account — see back-tenant
//     docs/features/challenge-refusal-status.md);
//   - CHECK:  POST /back-tenant/authentification/check-challenge.
// User-facing messages are French literals on purpose (the active react-intl catalog is `en`).

export const PHASE_CREATE = 'create';
export const PHASE_CHECK = 'check';

export const KIND_LOCKED = 'LOCKED';
export const KIND_PENDING = 'PENDING';
export const KIND_NOT_FOUND = 'NOT_FOUND';
export const KIND_TECHNICAL = 'TECHNICAL';
export const KIND_WRONG_ANSWER = 'WRONG_ANSWER';
export const KIND_EXPIRED = 'EXPIRED';

export const MESSAGES = {
    [KIND_LOCKED]: 'Votre compte est bloqué ou suspendu : la connexion est impossible. Contactez votre administrateur.',
    [KIND_PENDING]: "Votre convention n'est pas encore activée. Validez-la depuis l'e-mail HIA que vous avez reçu, puis reconnectez-vous.",
    [KIND_NOT_FOUND]: 'Compte introuvable. Vérifiez votre identifiant (adresse complète).',
    [KIND_TECHNICAL]: 'Le service de connexion est momentanément indisponible. Réessayez dans quelques instants.',
    [KIND_EXPIRED]: 'Le code a expiré. Recommencez la connexion.',
};

export const wrongAnswerMessage = (remaining) => {
    if (typeof remaining !== 'number' || remaining < 0) {
        return 'Réponse incorrecte.';
    }
    return `Réponse incorrecte — il vous reste ${remaining} tentative${remaining > 1 ? 's' : ''}.`;
};

const outcome = (kind, extra) => ({ kind, message: MESSAGES[kind], ...extra });

function describeCreateError(status, body) {
    switch (status) {
        case 423:
            return outcome(KIND_LOCKED);
        case 400:
            // Local BFF validation of the login (blank / too long): nothing was asked upstream.
            if (body && body.error === 'invalid_login') {
                return outcome(KIND_NOT_FOUND);
            }
            // Upstream 400: a BLOCKED convention rejected on the back-hia side surfaces as a
            // degraded 400 (known inconsistency of back-tenant). appName is always set by the BFF,
            // so this is the only functional cause left.
            console.warn('[showroom] challenge creation refused with status 400');
            return outcome(KIND_LOCKED);
        case 422:
            return outcome(KIND_PENDING);
        case 404:
            return outcome(KIND_NOT_FOUND);
        default:
            return outcome(KIND_TECHNICAL);
    }
}

function describeCheckError(status, body) {
    switch (status) {
        case 400: {
            const result = body && body.result;
            if (result === 'KO_BLOCKED') {
                return outcome(KIND_LOCKED);
            }
            if (result === 'KO') {
                const remaining = body.context ? body.context.countAttemps : undefined;
                if (remaining === 0) {
                    // No attempt left: the account is now blocked, the form must not come back.
                    return outcome(KIND_LOCKED);
                }
                return { kind: KIND_WRONG_ANSWER, message: wrongAnswerMessage(remaining), remaining };
            }
            return outcome(KIND_TECHNICAL);
        }
        case 410:
            return outcome(KIND_EXPIRED);
        // On the check path a locked account surfaces as 422 (MfaChallengeLockedException), and a
        // PENDING convention cannot get here (the challenge was created). 423 is also a lock.
        case 422:
        case 423:
            return outcome(KIND_LOCKED);
        default:
            return outcome(KIND_TECHNICAL);
    }
}

/**
 * Describe a failed challenge call.
 * @param {'create'|'check'} phase which call failed
 * @param {number|undefined} status HTTP status (undefined for a network error / timeout)
 * @param {*} body response body, if any
 * @returns {{kind: string, message: string, remaining?: number}}
 */
export function describeChallengeError(phase, status, body) {
    if (typeof status !== 'number') {
        return outcome(KIND_TECHNICAL);
    }
    return phase === PHASE_CHECK ? describeCheckError(status, body) : describeCreateError(status, body);
}
